import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { FC } from 'react';
import { IJob, IMappingFormSettingsResponse } from 'wisestep-api';

import RenderTextInput from '../../components/PersonalInfo/RenderTextInput';
import ISubmissionSettingContext from '../../types/ISubmissionSettingContext';
import ISubmissionSettingsResponse from '../../types/ISubmissionSettingsResponse';
import { ControlledTextInput } from '../ControlledTextInput';
import { Item } from '../Item';
import { Label } from '../Label';
import { WsTypoColors, WsTypography, WsTypoVariants } from '../WsTypography';

import ControlledSalaryDetails from './ControlledSalaryDetails';

export const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));

export interface ISalaryDetails {
  showTitle?: boolean;
  purpose?: 'mapping' | 'submission';
  submissionSetting?: ISubmissionSettingsResponse | ISubmissionSettingContext | null;
  mappingFormSettings?: IMappingFormSettingsResponse;
  job?: IJob | undefined;
  isOfferStage?: boolean;
  isValidContractType?: boolean;
}

const formLabel = (label: string, isRequired: boolean) => label + (isRequired ? '*' : '');
const allowedContractTypes = [
  'Contract',
  'Temporary',
  'Internship',
  'Freelance',
  'ContractToHireW2',
  'ContractW2',
  'ContractIndependent',
  'ContractToHireIndependent',
  'ContractCorpToCorp',
  'ContractToHireCorpToCorp',
  'PartTime',
  'FixedTermContract',
];
const SalaryDetails: FC<ISalaryDetails> = ({
  showTitle = true,
  purpose = 'submission',
  submissionSetting,
  mappingFormSettings,
  job,
  isOfferStage,
}) => {
  const isJobContract = allowedContractTypes.includes(job?.jobType ?? '');

  return (
    <>
      {showTitle && (
        <GridItem sx={{ mt: '12px' }}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
            Salary Details
          </WsTypography>
        </GridItem>
      )}
      {purpose == 'submission' && (
        <>
          {!submissionSetting?.currentCompensation?.hidden && (
            <GridItem sx={{ mt: '12px' }}>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Current Salary ',
                    submissionSetting?.currentCompensation?.required ?? false
                  )}
                </Label>
                <ControlledSalaryDetails name="currentSalary" />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.otherAllowance?.hidden && (
            <RenderTextInput
              name="otherAllowance"
              labelText={formLabel(
                'Other Allowance and Benefits ',
                submissionSetting?.otherAllowance?.required ?? false
              )}
              placeholder="Enter Other allowances and benefits"
            />
          )}
          {!submissionSetting?.expectedCompensation?.hidden && (
            <GridItem sx={{ mt: '12px' }}>
              <Grid item xs={8.5}>
                <Label>
                  {(job?.jobType
                    ? isJobContract
                      ? 'Submitted Pay Rate'
                      : 'Submitted Compensation'
                    : 'Expected Salary') +
                    `${submissionSetting?.expectedCompensation?.required ? ' *' : ''}`}
                </Label>
                <ControlledSalaryDetails name="expectedSalary" />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.clientBillRate?.hidden && isJobContract && (
            <GridItem sx={{ mt: '12px' }}>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Client Bill Rate',
                    submissionSetting?.clientBillRate?.required ?? false
                  )}
                </Label>
                <ControlledSalaryDetails name="clientBillRate" />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.offeredPayRate?.hidden && isOfferStage && (
            <GridItem sx={{ mt: '12px' }}>
              <Grid item xs={8.5}>
                <Label>
                  {(isJobContract ? 'Offered Pay Rate' : 'Offered Salary') +
                    `${submissionSetting?.offeredPayRate?.required ? ' *' : ''}`}
                </Label>
                <ControlledSalaryDetails name="offeredPayRate" />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.expectedAllowance?.hidden && (
            <RenderTextInput
              name="expectedAllowance"
              labelText={formLabel(
                'Expected Allowances and Benefits ',
                submissionSetting?.expectedAllowance?.required ?? false
              )}
              placeholder="Enter expected allowances and benefits"
            />
          )}
          {!submissionSetting?.payRateExpenses?.hidden && isJobContract && (
            <GridItem sx={{ mt: 1 }}>
              <Grid item xs={8.5}>
                <Label>
                  Pay Rate Expenses
                  {submissionSetting?.payRateExpenses?.required ? ' *' : ''}
                </Label>
                <ControlledSalaryDetails name="payRateExpenses" />
              </Grid>
            </GridItem>
          )}
        </>
      )}
      {purpose == 'mapping' && (
        <>
          {mappingFormSettings?.currentSalary.show && (
            <GridItem sx={{ mt: '12px' }}>
              <Label>
                {formLabel('Current Salary', mappingFormSettings?.currentSalary.mandatory)}
              </Label>
              <ControlledSalaryDetails name="currentSalary" />
            </GridItem>
          )}
          {mappingFormSettings?.otherAllowance.show && (
            <GridItem>
              <ControlledTextInput
                name="otherAllowance"
                labelText={formLabel(
                  'Other allowances and benefits',
                  mappingFormSettings?.otherAllowance.mandatory
                )}
                fullWidth
                id="other_allowance"
                placeholder="Enter other allowances and benefits"
                variant="outlined"
                autoComplete="other_allowance"
                required={mappingFormSettings?.otherAllowance.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.expectedSalary.show && (
            <GridItem sx={{ mt: '12px' }}>
              <Label>
                {formLabel(
                  job?.jobType
                    ? job.jobType == 'Contract'
                      ? 'Submitted Pay Rate'
                      : 'Submitted Salary'
                    : 'Expected Salary',
                  mappingFormSettings?.expectedSalary.mandatory
                )}
              </Label>
              <ControlledSalaryDetails name="expectedSalary" />
            </GridItem>
          )}
          {mappingFormSettings?.expectedAllowance.show && (
            <GridItem>
              <ControlledTextInput
                name="expectedAllowance"
                labelText={formLabel(
                  'Expected allowances and benefits',
                  mappingFormSettings?.expectedAllowance.mandatory
                )}
                fullWidth
                id="expected_allowance"
                placeholder="Enter expected allowances and benefits"
                variant="outlined"
                autoComplete="expected_allowance"
                required={mappingFormSettings?.expectedAllowance.mandatory}
              />
            </GridItem>
          )}
        </>
      )}
    </>
  );
};

export default SalaryDetails;
